import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Calendar, Heart, Ruler, Weight, MapPin, FileText, Image as ImageIcon, User } from "lucide-react"

interface DetalhesPetModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  pet: any
}

export default function DetalhesPetModal({ open, onOpenChange, pet }: DetalhesPetModalProps) {
  if (!pet) return null

  // Debug: Log para verificar dados do pet
  console.log('Pet data in modal:', JSON.stringify(pet, null, 2))
  console.log('Photo URL:', pet.photo_url)

  const formatDate = (date: string) => {
    return new Date(date).toLocaleDateString('pt-BR')
  }

  const getAgeText = (birthDate: string) => {
    const birth = new Date(birthDate)
    const today = new Date()
    const ageInMonths = (today.getFullYear() - birth.getFullYear()) * 12 + (today.getMonth() - birth.getMonth())
    
    if (ageInMonths < 12) {
      return `${ageInMonths} meses`
    }
    const years = Math.floor(ageInMonths / 12)
    return `${years} ano${years > 1 ? 's' : ''}`
  }

  const getSexIcon = (sex: string) => {
    if (sex === 'macho') return '♂'
    if (sex === 'femea') return '♀'
    return '•'
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center space-x-2">
            <Heart className="h-5 w-5 text-red-500" />
            <span>{pet.name}</span>
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6">
          {/* Foto do Pet */}
          {pet.photo_url ? (
            <div className="flex justify-center">
              <div className="relative">
                <img
                  src={pet.photo_url}
                  alt={pet.name}
                  className="w-full max-w-md h-64 object-cover rounded-lg shadow-md"
                  onError={(e) => {
                    console.error('Erro ao carregar imagem:', pet.photo_url);
                    e.currentTarget.style.display = 'none';
                    const placeholder = e.currentTarget.nextElementSibling as HTMLElement;
                    if (placeholder) {
                      placeholder.style.display = 'flex';
                    }
                  }}
                />
                <div className="w-full max-w-md h-64 bg-gray-100 rounded-lg shadow-md items-center justify-center hidden">
                  <div className="text-center text-gray-500">
                    <ImageIcon className="h-12 w-12 mx-auto mb-2" />
                    <p>Erro ao carregar foto</p>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="flex justify-center">
              <div className="w-full max-w-md h-64 bg-gray-100 rounded-lg shadow-md flex items-center justify-center">
                <div className="text-center text-gray-500">
                  <ImageIcon className="h-12 w-12 mx-auto mb-2" />
                  <p>Sem foto disponível</p>
                </div>
              </div>
            </div>
          )}

          {/* Informações Básicas */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-4">
              <div>
                <h3 className="font-semibold text-lg mb-2">Informações Básicas</h3>
                <div className="space-y-2">
                  <div className="flex items-center space-x-2">
                    <span className="text-2xl">{pet.type === 'cao' ? '🐕' : pet.type === 'gato' ? '🐱' : '🐾'}</span>
                    <span className="font-medium">{pet.type.charAt(0).toUpperCase() + pet.type.slice(1)}</span>
                  </div>
                  
                  {pet.breed && (
                    <p><span className="font-medium">Raça:</span> {pet.breed}</p>
                  )}
                  
                  {pet.color && (
                    <p><span className="font-medium">Cor:</span> {pet.color}</p>
                  )}
                  
                  {pet.sex && (
                    <p className="flex items-center space-x-1">
                      <span className="font-medium">Sexo:</span>
                      <span>{getSexIcon(pet.sex)} {pet.sex.charAt(0).toUpperCase() + pet.sex.slice(1)}</span>
                    </p>
                  )}
                </div>
              </div>

              {/* Informações do Dono */}
              {pet.owner?.full_name && (
                <div>
                  <h4 className="font-medium flex items-center space-x-1 mb-2">
                    <User className="h-4 w-4" />
                    <span>Dono</span>
                  </h4>
                  <p>{pet.owner.full_name}</p>
                </div>
              )}

              {/* Localização */}
              {pet.unit_number && (
                <div>
                  <h4 className="font-medium flex items-center space-x-1 mb-2">
                    <MapPin className="h-4 w-4" />
                    <span>Localização</span>
                  </h4>
                  <p>Unidade: {pet.unit_number}</p>
                </div>
              )}
            </div>

            <div className="space-y-4">
              {/* Características Físicas */}
              <div>
                <h4 className="font-medium mb-2">Características</h4>
                <div className="space-y-2">
                  {pet.birth_date && (
                    <div className="flex items-center space-x-2">
                      <Calendar className="h-4 w-4 text-muted-foreground" />
                      <span className="text-sm">
                        <span className="font-medium">Idade:</span> {getAgeText(pet.birth_date)}
                      </span>
                    </div>
                  )}
                  
                  {pet.size && (
                    <div className="flex items-center space-x-2">
                      <Ruler className="h-4 w-4 text-muted-foreground" />
                      <span className="text-sm">
                        <span className="font-medium">Porte:</span> {pet.size.charAt(0).toUpperCase() + pet.size.slice(1)}
                      </span>
                    </div>
                  )}
                  
                  {pet.weight && (
                    <div className="flex items-center space-x-2">
                      <Weight className="h-4 w-4 text-muted-foreground" />
                      <span className="text-sm">
                        <span className="font-medium">Peso:</span> {pet.weight}kg
                      </span>
                    </div>
                  )}
                </div>
              </div>

              {/* Status de Visibilidade */}
              <div>
                <h4 className="font-medium mb-2">Visibilidade</h4>
                <Badge variant={pet.visible_to_residents ? "default" : "secondary"}>
                  {pet.visible_to_residents ? "Visível para moradores" : "Privado"}
                </Badge>
              </div>
            </div>
          </div>

          {/* Observações */}
          {pet.observations && (
            <div>
              <h4 className="font-medium flex items-center space-x-1 mb-2">
                <FileText className="h-4 w-4" />
                <span>Observações</span>
              </h4>
              <div className="bg-muted p-3 rounded-md">
                <p className="text-sm">{pet.observations}</p>
              </div>
            </div>
          )}

          {/* Data de Nascimento */}
          {pet.birth_date && (
            <div>
              <h4 className="font-medium mb-2">Data de Nascimento</h4>
              <p className="text-sm text-muted-foreground">{formatDate(pet.birth_date)}</p>
            </div>
          )}

          {/* Carteira de Vacinação */}
          {pet.vaccination_card_url && (
            <div className="flex justify-center pt-4">
              <Button variant="outline" asChild>
                <a href={pet.vaccination_card_url} target="_blank" rel="noopener noreferrer">
                  <ImageIcon className="h-4 w-4 mr-2" />
                  Ver Carteira de Vacinação
                </a>
              </Button>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  )
}